import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapPin, Navigation, ShieldAlert, CheckCircle2, Globe,
  Search, FileText, RefreshCw, AlertTriangle, ExternalLink,
  UploadCloud, Sparkles, X, Map as MapIcon, Loader2, Camera,
  User, Users, Scale, Home, Briefcase, Heart, Percent,
} from 'lucide-react';
import { buscarCoordenadas, construirEnderecoCompleto } from '../../lib/googleMapsUtils';
import { normalizarLogradouro } from './normalizacao/normalizarLogradouro';

/**
 * PGLP | INGESTÃO DE IMÓVEL — v3.2.0
 *
 * NOVIDADES v3.2.0:
 *   ✔ CEP via cadeia de fallback: Google → ViaCEP → Nominatim
 *   ✔ ViaCEP corrige bairro errado do OCR automaticamente
 *   ✔ Normaliza logradouro para busca no ViaCEP (remove "Rua", "Av.", etc)
 *
 * v3.1.0:
 *   ✔ Extrai proprietários atuais com QUALIFICAÇÃO COMPLETA
 *   ✔ Extrai matrícula (RGI) estruturada
 *   ✔ Propaga rgi/cartografico para fase IDENTIFICAÇÃO
 *
 * v3.0.0:
 *   ✔ OCR via Gemini
 */

const NUMEROS_ESPECIAIS = ['S/N', 'SN', 'SEM NUMERO', 'SEM NÚMERO', 'SEM NUMERAÇÃO', 'SEM NUMERACAO'];

function numeroValido(valor) {
  const v = String(valor || '').trim().toUpperCase();
  if (!v) return false;
  if (/\d/.test(v)) return true;
  return NUMEROS_ESPECIAIS.some(esp => v.includes(esp));
}

function montarComplementoRegistral(lote, quadra, setor) {
  const l = String(lote || '').trim();
  const q = String(quadra || '').trim();
  const s = String(setor || '').trim();
  const partes = [];
  if (l) partes.push(`LOTE: ${l}`);
  if (q) partes.push(`Quadra: ${q}`);
  if (s) partes.push(`Setor: ${s}`);
  return partes.join(' ');
}

function montarUrlMapa(lat, lng, fatorBbox = 0.0015) {
  if (!lat || !lng) return '';
  const LAT = Number(lat);
  const LNG = Number(lng);
  if (Number.isNaN(LAT) || Number.isNaN(LNG)) return '';
  const lonMin = (LNG - fatorBbox).toFixed(7);
  const latMin = (LAT - fatorBbox).toFixed(7);
  const lonMax = (LNG + fatorBbox).toFixed(7);
  const latMax = (LAT + fatorBbox).toFixed(7);
  return (
    `https://www.openstreetmap.org/export/embed.html?bbox=` +
    `${lonMin}%2C${latMin}%2C${lonMax}%2C${latMax}` +
    `&layer=mapnik&marker=${LAT.toFixed(7)}%2C${LNG.toFixed(7)}`
  );
}

/* ============================================================
   CEP — Camada 1: Google Geocoding
============================================================ */
async function buscarCepViaGeocoding({ logradouro, numero, bairro, cidade, uf }) {
  if (!logradouro || !cidade || !uf) return null;
  try {
    const enderecoCompleto = [logradouro, numero, bairro, cidade, uf].filter(Boolean).join(', ');
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY || '';
    if (!apiKey) return null;

    const resp = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(enderecoCompleto)}&components=country:BR&key=${apiKey}`
    );
    const data = await resp.json();
    if (data.status !== 'OK' || !data.results?.length) return null;

    const comps = data.results[0].address_components || [];
    const cepComp = comps.find((c) => c.types.includes('postal_code'));
    if (!cepComp) return null;
    return cepComp.short_name.replace(/\D/g, '').slice(0, 8);
  } catch {
    return null;
  }
}

/* ============================================================
   CEP — Camada 2: ViaCEP (fonte oficial dos Correios)
============================================================ */
function normalizarLogradouroParaViaCep(logradouro) {
  return String(logradouro || '')
    .replace(/^(RUA|R\.|AVENIDA|AV\.|ALAMEDA|AL\.|TRAVESSA|TV\.|ESTRADA|EST\.|RODOVIA|ROD\.|PRAÇA|PC\.|PRA[AÇ]A)\s+/i, '')
    .replace(/[.,;:]+$/g, '')
    .trim();
}

async function buscarCepViaViaCep({ logradouro, cidade, uf }) {
  if (!logradouro || !cidade || !uf) return null;

  try {
    const rua = normalizarLogradouroParaViaCep(logradouro);
    const url = `https://viacep.com.br/ws/${encodeURIComponent(uf)}/${encodeURIComponent(cidade)}/${encodeURIComponent(rua)}/json/`;

    const resp = await fetch(url);
    const data = await resp.json();

    if (!Array.isArray(data) || data.length === 0) return null;

    if (data.length === 1) {
      return {
        cep: data[0].cep.replace(/\D/g, ''),
        bairro: data[0].bairro || null,
        logradouro: data[0].logradouro || null,
      };
    }

    return {
      cep: data[0].cep.replace(/\D/g, ''),
      bairro: data[0].bairro || null,
      logradouro: data[0].logradouro || null,
      multiplos: true,
    };
  } catch {
    return null;
  }
}

/* ============================================================
   CEP — Camada 3: Nominatim (último recurso)
============================================================ */
async function buscarCepViaNominatim(lat, lng) {
  if (!lat || !lng) return null;
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'Accept': 'application/json', 'User-Agent': 'MonteHermom-PGLP/1.0' } }
    );
    const data = await resp.json();
    const cepRaw = data?.address?.postcode;
    if (!cepRaw) return null;
    const cepLimpo = String(cepRaw).replace(/\D/g, '').slice(0, 8);
    return cepLimpo.length === 8 ? cepLimpo : null;
  } catch {
    return null;
  }
}

/* ============================================================
   CEP — Orquestrador com fallback em cadeia
============================================================ */
async function buscarCepInteligente({ logradouro, numero, bairro, cidade, uf, lat, lng }) {
  // Camada 1: Google Geocoding
  const cepGoogle = await buscarCepViaGeocoding({ logradouro, numero, bairro, cidade, uf });
  if (cepGoogle) return { cep: cepGoogle, fonte: 'GOOGLE', bairro: null };

  // Camada 2: ViaCEP (Correios)
  const viaCep = await buscarCepViaViaCep({ logradouro, cidade, uf });
  if (viaCep?.cep) {
    return {
      cep: viaCep.cep,
      fonte: 'VIACEP',
      bairro: viaCep.bairro,
      logradouro: viaCep.logradouro,
    };
  }

  // Camada 3: Nominatim
  if (lat && lng) {
    const cepOSM = await buscarCepViaNominatim(lat, lng);
    if (cepOSM) return { cep: cepOSM, fonte: 'NOMINATIM', bairro: null };
  }

  return null;
}

export default function ImovelParadigma_Ingestao({
  reportNumber,
  laudoUuid = null,
  laudoId = null,
  dadosImovel: dadosRecebidos = {},
  coordenadas: coordenadasRecebidas = {},
  onChange,
  onComplete,
}) {
  const [dadosImovel, setDadosImovel] = useState(() => ({
    cep:         dadosRecebidos.cep || '',
    logradouro:  dadosRecebidos.logradouro || '',
    numero:      dadosRecebidos.numero || '',
    complemento: dadosRecebidos.complemento || '',
    bairro:      dadosRecebidos.bairro || '',
    cidade:      dadosRecebidos.cidade || '',
    uf:          dadosRecebidos.uf || '',
    lote:        dadosRecebidos.lote || '',
    quadra:      dadosRecebidos.quadra || '',
    setor:       dadosRecebidos.setor || '',
    semNumero:   Boolean(dadosRecebidos.semNumero),
    lat:         dadosRecebidos.lat || coordenadasRecebidas.lat || '',
    lng:         dadosRecebidos.lng || coordenadasRecebidas.lng || '',
    geocodificacao: dadosRecebidos.geocodificacao || null,
    rgi:            dadosRecebidos.rgi || '',
    cartografico:   dadosRecebidos.cartografico || '',
  }));

  const [dadosMatricula, setDadosMatricula] = useState(
    dadosRecebidos.dadosMatricula || null
  );
  const [proprietarios, setProprietarios] = useState(
    dadosRecebidos.proprietarios || []
  );

  const [geocodificacao, setGeocodificacao] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erro, setErro] = useState('');
  const [avisoOCR, setAvisoOCR] = useState(null);
  const [enderecoConfirmado, setEnderecoConfirmado] = useState(false);
  const [mapaValidado, setMapaValidado] = useState(false);
  const [arquivoIngestao, setArquivoIngestao] = useState(null);
  const [processandoDoc, setProcessandoDoc] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!dadosRecebidos || Object.keys(dadosRecebidos).length === 0) return;
    setDadosImovel(prev => ({ ...prev, ...dadosRecebidos }));
  }, [dadosRecebidos]);

  useEffect(() => {
    if (!dadosImovel.semNumero) return;
    const novoComplemento = montarComplementoRegistral(
      dadosImovel.lote,
      dadosImovel.quadra,
      dadosImovel.setor
    );
    if (novoComplemento !== dadosImovel.complemento) {
      setDadosImovel(prev => ({ ...prev, complemento: novoComplemento }));
    }
  }, [dadosImovel.lote, dadosImovel.quadra, dadosImovel.setor, dadosImovel.semNumero]);

  const enderecoMinimoOk = Boolean(
    (dadosImovel.logradouro || '').trim() &&
    (dadosImovel.cidade || '').trim() &&
    (dadosImovel.uf || '').trim()
  );

  const numeroOk = dadosImovel.semNumero
    ? Boolean(
        (dadosImovel.lote || '').trim() &&
        (dadosImovel.quadra || '').trim() &&
        (dadosImovel.setor || '').trim()
      )
    : numeroValido(dadosImovel.numero);

  const geocodificacaoOk =
    Boolean(dadosImovel.lat) &&
    Boolean(dadosImovel.lng) &&
    dadosImovel.geocodificacao?.fonte === 'GOOGLE_GEOCODING';

  const atualizarCampo = (campo, valor) => {
    const atualizado = {
      ...dadosImovel,
      [campo]: valor,
      lat: '', lng: '', geocodificacao: null,
    };
    setDadosImovel(atualizado);
    setGeocodificacao(null);
    setEnderecoConfirmado(false);
    setMapaValidado(false);
    setErro('');
    if (typeof onChange === 'function') onChange(atualizado);
  };

  const alternarSemNumero = () => {
    const novoEstado = !dadosImovel.semNumero;
    const complementoNovo = novoEstado
      ? montarComplementoRegistral(dadosImovel.lote, dadosImovel.quadra, dadosImovel.setor)
      : '';
    const atualizado = {
      ...dadosImovel,
      semNumero: novoEstado,
      numero: novoEstado ? 'SEM NÚMERO' : '',
      complemento: complementoNovo,
      lote: novoEstado ? dadosImovel.lote : '',
      quadra: novoEstado ? dadosImovel.quadra : '',
      setor: novoEstado ? dadosImovel.setor : '',
      lat: '', lng: '', geocodificacao: null,
    };
    setDadosImovel(atualizado);
    setGeocodificacao(null);
    setEnderecoConfirmado(false);
    setMapaValidado(false);
    setErro('');
    if (typeof onChange === 'function') onChange(atualizado);
  };

  const normalizarNoBlur = (campo) => {
    setDadosImovel(prev => {
      let atualizado = { ...prev };
      if (campo === 'logradouro') atualizado.logradouro = normalizarLogradouro(prev.logradouro || '');
      if (campo === 'uf')         atualizado.uf = String(prev.uf || '').toUpperCase().slice(0, 2);
      if (campo === 'numero' && !prev.semNumero) atualizado.numero = String(prev.numero || '').trim().toUpperCase();
      if (campo === 'lote')       atualizado.lote = String(prev.lote || '').trim().toUpperCase();
      if (campo === 'quadra')     atualizado.quadra = String(prev.quadra || '').trim().toUpperCase();
      if (campo === 'setor')      atualizado.setor = String(prev.setor || '').trim().toUpperCase();
      if (typeof onChange === 'function') onChange(atualizado);
      return atualizado;
    });
  };

  const consultarCep = async (cepRaw) => {
    const cepLimpo = String(cepRaw || '').replace(/\D/g, '').slice(0, 8);
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    setErro('');
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await resp.json();
      if (data.erro) throw new Error('CEP não encontrado.');
      const atualizado = {
        ...dadosImovel,
        cep: cepLimpo,
        logradouro: normalizarLogradouro(data.logradouro || ''),
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        uf: (data.uf || '').toUpperCase(),
        lat: '', lng: '', geocodificacao: null,
      };
      setDadosImovel(atualizado);
      setGeocodificacao(null);
      setEnderecoConfirmado(false);
      setMapaValidado(false);
      if (typeof onChange === 'function') onChange(atualizado);
    } catch (e) {
      setErro(e?.message || 'Falha ao consultar o CEP.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const aceitarDocumento = (file) => {
    if (!file) return;
    const tipos = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!tipos.includes(file.type)) {
      setErro('Somente PDF, JPG, PNG ou WEBP são aceitos.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setErro('O arquivo excede 20 MB.');
      return;
    }
    setErro('');
    setAvisoOCR(null);
    setArquivoIngestao(file);
  };

  /* ============================================================
     PROCESSAMENTO VIA GEMINI
  ============================================================ */
  const processarDocumento = async () => {
    if (!arquivoIngestao) return;
    setProcessandoDoc(true);
    setErro('');
    setAvisoOCR(null);

    try {
      const fd = new FormData();
      fd.append('arquivo', arquivoIngestao);
      if (reportNumber) fd.append('laudo_uuid', reportNumber);
      if (laudoId) fd.append('laudo_id', String(laudoId));

      const resp = await fetch('/api/imovel/ocr_gemini.php', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });

      const data = await resp.json();

      if (!data.success) {
        setErro(data.erro || 'Falha ao processar o documento.');
        return;
      }

      const end      = data.endereco || {};
      const areas    = data.areas || {};
      const mat      = data.matricula || {};
      const cad      = data.cartografico || data.cadastro_cartografico || {};
      const props    = Array.isArray(data.proprietarios) ? data.proprietarios : [];

      const atualizado = { ...dadosImovel };

      const preencher = (campo, valor) => {
        if (!valor) return;
        if (String(atualizado[campo] || '').trim()) return;
        atualizado[campo] = String(valor);
      };

      /* ---------- Endereço ---------- */
      preencher('cep', end.cep);
      preencher('logradouro', end.logradouro);
      preencher('numero', end.numero);
      preencher('complemento', end.complemento);
      preencher('bairro', end.bairro);
      preencher('cidade', end.cidade);
      preencher('uf', end.uf);

      /* ---------- Áreas ---------- */
      if (areas.terreno && !String(atualizado.areaTerreno || '').trim()) {
        atualizado.areaTerreno = String(areas.terreno);
      }
      if (areas.frente && !String(atualizado.frente || '').trim()) {
        atualizado.frente = String(areas.frente);
      }

      /* ---------- RGI / Matrícula ---------- */
      if (mat.numero) {
        atualizado.rgi = String(mat.numero);
        atualizado.matricula = String(mat.numero);
      }

      /* ---------- Cadastro cartográfico ---------- */
      if (cad.codigo) {
        atualizado.cartografico = String(cad.codigo);
      }
      if (cad.setor && !atualizado.setor) atualizado.setor = String(cad.setor);
      if (cad.quadra && !atualizado.quadra) atualizado.quadra = String(cad.quadra);
      if (cad.lote && !atualizado.lote) atualizado.lote = String(cad.lote);

      /* ============================================================
         FALLBACK DE CEP — cadeia: Google → ViaCEP → Nominatim
      ============================================================ */
      let cepInferido = false;
      if (!atualizado.cep && atualizado.logradouro && atualizado.cidade && atualizado.uf) {
        const resultado = await buscarCepInteligente({
          logradouro: atualizado.logradouro,
          numero:     atualizado.numero,
          bairro:     atualizado.bairro,
          cidade:     atualizado.cidade,
          uf:         atualizado.uf,
          lat:        atualizado.lat || null,
          lng:        atualizado.lng || null,
        });

        if (resultado?.cep) {
          atualizado.cep = resultado.cep;
          cepInferido = true;

          // ViaCEP pode corrigir bairro errado do OCR
          if (resultado.bairro && resultado.bairro !== atualizado.bairro) {
            atualizado.bairroOriginalOCR = atualizado.bairro;
            atualizado.bairro = resultado.bairro;
          }

          atualizado._ocr = {
            ...(atualizado._ocr || {}),
            cep_fonte: resultado.fonte,
          };
        }
      }

      /* ---------- Metadados do OCR ---------- */
      atualizado._ocr = {
        ...(atualizado._ocr || {}),
        tipo_documento: data.tipo_documento,
        confianca: data.confianca_global,
        processado_em: new Date().toISOString(),
        arquivo: data.arquivo,
        cep_inferido_via_fallback: cepInferido,
      };

      /* ---------- Estruturados ---------- */
      atualizado.dadosMatricula = mat && Object.keys(mat).length
        ? {
            numero: mat.numero || null,
            cartorio: mat.cartorio || null,
            comarca: mat.comarca || null,
            uf: mat.uf || null,
            data_registro: mat.data_registro || null,
            livro: mat.livro || null,
            folha: mat.folha || null,
          }
        : null;

      atualizado.proprietarios = props.map((p, idx) => ({
        id: `prop-${Date.now()}-${idx}`,
        nome: p.nome || '',
        cpf: p.cpf || null,
        rg: p.rg || null,
        orgao_emissor: p.orgao_emissor || null,
        profissao: p.profissao || null,
        nacionalidade: p.nacionalidade || null,
        estado_civil: p.estado_civil || null,
        regime_bens: p.regime_bens || null,
        conjuge: p.conjuge || null,
        conjuge_cpf: p.conjuge_cpf || null,
        endereco_completo: p.endereco_completo || null,
        percentual: p.percentual ?? null,
        forma_aquisicao: p.forma_aquisicao || null,
        data_aquisicao: p.data_aquisicao || null,
        averbacao_origem: p.averbacao_origem || null,
      }));

      setDadosImovel(atualizado);
      setDadosMatricula(atualizado.dadosMatricula);
      setProprietarios(atualizado.proprietarios);
      setArquivoIngestao(null);
      setEnderecoConfirmado(false);
      setMapaValidado(false);

      if (typeof onChange === 'function') onChange(atualizado);

      /* ---------- Aviso de resumo ---------- */
      const camposExtraidos = [];
      if (end.cep) camposExtraidos.push('CEP');
      if (cepInferido) camposExtraidos.push('CEP (inferido)');
      if (end.logradouro) camposExtraidos.push('logradouro');
      if (end.numero) camposExtraidos.push('número');
      if (end.bairro) camposExtraidos.push('bairro');
      if (end.cidade) camposExtraidos.push('cidade');
      if (mat.numero) camposExtraidos.push('matrícula (RGI)');
      if (mat.cartorio) camposExtraidos.push('cartório');
      if (cad.codigo) camposExtraidos.push('cadastro');
      if (areas.terreno) camposExtraidos.push('área');

      setAvisoOCR({
        tipo: data.tipo_documento,
        confianca: data.confianca_global,
        campos: camposExtraidos,
        proprietarios: props.length,
        observacoes: data.observacoes,
      });
    } catch (e) {
      setErro(e?.message || 'Falha ao processar o documento.');
    } finally {
      setProcessandoDoc(false);
    }
  };

  const geocodificar = async () => {
    if (!enderecoMinimoOk) { setErro('Informe logradouro, cidade e UF.'); return; }
    if (!numeroOk) {
      setErro(dadosImovel.semNumero
        ? 'Preencha o Lote, a Quadra e o Setor.'
        : 'Informe o número do imóvel.');
      return;
    }

    setCarregando(true);
    setErro('');
    setEnderecoConfirmado(false);
    setMapaValidado(false);

    try {
      const resultado = await buscarCoordenadas({
        endereco: dadosImovel.logradouro,
        numero: dadosImovel.numero,
        bairro: dadosImovel.bairro,
        cidade: dadosImovel.cidade,
        uf: dadosImovel.uf,
        cep: dadosImovel.cep,
      });

      const lat = resultado?.location?.lat;
      const lng = resultado?.location?.lng;
      if (lat == null || lng == null) throw new Error('O Google não retornou coordenadas.');

      const atualizado = {
        ...dadosImovel,
        lat: String(lat),
        lng: String(lng),
        geocodificacao: {
          fonte: resultado.fonte || 'GOOGLE_GEOCODING',
          place_id: resultado.place_id || null,
          formatted_address: resultado.formatted_address || null,
          partial_match: Boolean(resultado.partial_match),
          location_type: resultado.location_type || null,
        },
      };
      setDadosImovel(atualizado);
      setGeocodificacao(resultado);
      if (typeof onChange === 'function') onChange(atualizado);
    } catch (e) {
      setErro(e?.message || 'Não foi possível obter a localização.');
      setGeocodificacao(null);
    } finally {
      setCarregando(false);
    }
  };

  const finalizar = () => {
    if (!enderecoMinimoOk) { setErro('Preencha logradouro, cidade e UF.'); return; }
    if (!numeroOk) {
      setErro(dadosImovel.semNumero
        ? 'Preencha o Lote, a Quadra e o Setor.'
        : 'Informe o número.');
      return;
    }
    if (!geocodificacaoOk) { setErro('A localização precisa ser obtida antes de continuar.'); return; }
    if (!enderecoConfirmado || !mapaValidado) { setErro('Confirme o endereço e valide o mapa.'); return; }
    if (typeof onComplete === 'function') {
      onComplete({
        ...dadosImovel,
        dadosMatricula,
        proprietarios,
      });
    }
  };

  const mapaUrl = montarUrlMapa(dadosImovel.lat, dadosImovel.lng);

  const linkGoogleMaps = dadosImovel.lat && dadosImovel.lng
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${dadosImovel.lat},${dadosImovel.lng}`
      )}`
    : '';

  const inputBase =
    'w-full h-11 bg-slate-950 border border-white/10 rounded-xl px-3.5 font-bold text-white text-[12px] outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 placeholder:font-bold';

  return (
    <div className="animate-in fade-in zoom-in-95 duration-500 w-full space-y-4">

      {/* HEADER BARRA */}
      <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg flex flex-col lg:flex-row justify-between gap-3 lg:items-center">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-slate-950 rounded-xl flex items-center justify-center border border-white/10 shrink-0">
            <Globe className="text-amber-400" size={16} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-black italic tracking-tight text-white uppercase leading-none">
              Ingestão de Dados
            </h2>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[7px] bg-amber-400 text-slate-950 px-2 py-0.5 rounded-full font-black uppercase tracking-widest">
                Endereço do Imóvel
              </span>
              <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest italic">
                Laudo: {reportNumber || 'não informado'}
              </span>
              {dadosImovel.semNumero && (
                <span className="text-[7px] bg-slate-950 text-amber-400 px-2 py-0.5 rounded-full font-black uppercase tracking-widest border border-amber-400/30">
                  Lote + Quadra + Setor
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <MapPin size={12} className={geocodificacaoOk ? 'text-emerald-500' : 'text-slate-500'} />
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            {geocodificacaoOk ? 'Coordenadas obtidas' : 'Aguardando geocodificação'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">

        {/* MAPA */}
        <div className="col-span-12 lg:col-span-7">
          <div className="bg-slate-900 p-4 rounded-2xl border border-white/5 shadow-xl">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="text-amber-400" size={14} />
                <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                  Georreferenciamento
                </h3>
              </div>
              {dadosImovel.lat && dadosImovel.lng && (
                <span className="text-[9px] font-mono font-black text-amber-400 bg-slate-950 px-3 py-1 rounded-full border border-white/5">
                  {dadosImovel.lat} | {dadosImovel.lng}
                </span>
              )}
            </div>

            <div className="w-full h-[380px] bg-slate-950 rounded-2xl border border-white/5 overflow-hidden relative">
              {carregando && (
                <div className="absolute inset-0 z-20 bg-slate-950/90 flex flex-col items-center justify-center gap-3">
                  <RefreshCw className="animate-spin text-amber-400" size={28} />
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Consultando Google…</p>
                </div>
              )}

              {mapaUrl ? (
                <iframe
                  title="Localização do imóvel"
                  src={mapaUrl}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                  <Navigation size={36} className="text-slate-700 mb-3" />
                  <p className="text-slate-400 font-black text-[10px] uppercase tracking-widest">
                    Aguardando geocodificação
                  </p>
                  <p className="text-slate-600 text-[9px] font-bold mt-1">
                    O mapa aparece após localizar o endereço
                  </p>
                </div>
              )}

              {mapaUrl && linkGoogleMaps && (
                <a
                  href={linkGoogleMaps}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute top-3 right-3 bg-slate-950/95 border border-white/10 px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest text-amber-400 shadow-lg flex items-center gap-1.5 hover:bg-slate-800 transition-all"
                >
                  Abrir no Google Maps <ExternalLink size={10} />
                </a>
              )}
            </div>

            {geocodificacao && (
              <div className="mt-3 p-3 bg-slate-950 rounded-xl border border-white/5 space-y-1.5">
                <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest">
                  Resultado da geocodificação
                </p>
                <p className="text-[11px] font-bold text-white">
                  {geocodificacao.formatted_address || 'Endereço formatado não informado'}
                </p>
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => setMapaValidado(v => !v)}
                disabled={!geocodificacaoOk}
                className={`px-5 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 ${
                  mapaValidado
                    ? 'bg-emerald-500 text-white'
                    : 'bg-amber-400 text-slate-950 hover:bg-white disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed'
                }`}
              >
                {mapaValidado ? <CheckCircle2 size={12} /> : <Search size={12} />}
                {mapaValidado ? 'Localização Validada' : 'Validar Localização'}
              </button>
            </div>
          </div>
        </div>

        {/* FORMULÁRIO */}
        <div className="col-span-12 lg:col-span-5">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-3">

            {/* DROPZONE — OCR */}
            <div className="pb-3 border-b border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={12} className="text-amber-400" />
                <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest">
                  Ingestão Automática · IA
                </span>
              </div>
              {!arquivoIngestao ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-amber-400/30 hover:border-amber-400/60 rounded-xl py-4 transition-all flex flex-col items-center gap-1.5 bg-amber-400/5 hover:bg-amber-400/10"
                >
                  <UploadCloud size={18} className="text-amber-400" />
                  <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Subir Petição / Matrícula / IPTU
                  </span>
                  <span className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">
                    PDF ou imagem · IA lê e extrai os dados
                  </span>
                </button>
              ) : (
                <div className="bg-slate-950 border border-amber-400/30 rounded-xl p-3 flex items-center gap-3">
                  <FileText size={16} className="text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-white truncate">{arquivoIngestao.name}</p>
                    <p className="text-[8px] text-slate-500 font-black uppercase">
                      {(arquivoIngestao.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  {!processandoDoc && (
                    <>
                      <button
                        onClick={processarDocumento}
                        className="px-3 py-1.5 rounded-lg bg-amber-400 text-slate-950 text-[9px] font-black uppercase tracking-widest flex items-center gap-1"
                      >
                        <Sparkles size={10} /> Processar
                      </button>
                      <button
                        onClick={() => setArquivoIngestao(null)}
                        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400"
                      >
                        <X size={12} />
                      </button>
                    </>
                  )}
                  {processandoDoc && (
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="text-amber-400 animate-spin" />
                      <span className="text-[9px] font-black text-amber-400 uppercase">IA lendo…</span>
                    </div>
                  )}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => aceitarDocumento(e.target.files?.[0] || null)}
              />

              {avisoOCR && (
                <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-emerald-400" />
                    <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">
                      IA extraiu dados de {avisoOCR.tipo}
                      {avisoOCR.confianca != null && ` · ${Math.round(avisoOCR.confianca * 100)}%`}
                    </span>
                  </div>
                  {avisoOCR.campos?.length > 0 && (
                    <p className="text-[8px] text-slate-400 leading-relaxed">
                      Campos: <span className="text-emerald-400 font-black">{avisoOCR.campos.join(', ')}</span>
                    </p>
                  )}
                  {avisoOCR.proprietarios > 0 && (
                    <p className="text-[8px] text-slate-400">
                      Proprietários identificados: <span className="text-emerald-400 font-black">{avisoOCR.proprietarios}</span>
                    </p>
                  )}
                  {avisoOCR.observacoes && (
                    <p className="text-[8px] text-slate-500 italic leading-relaxed">
                      {avisoOCR.observacoes}
                    </p>
                  )}
                </div>
              )}

              {/* CARDS ESTRUTURADOS */}
              {dadosMatricula && (
                <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/30 rounded-xl space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Scale size={12} className="text-blue-400" />
                    <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">
                      Matrícula (RGI) Identificada
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[9px]">
                    {dadosMatricula.numero && (
                      <p className="text-slate-400">
                        Nº <span className="text-white font-black">{dadosMatricula.numero}</span>
                      </p>
                    )}
                    {dadosMatricula.cartorio && (
                      <p className="text-slate-400 truncate" title={dadosMatricula.cartorio}>
                        Cartório <span className="text-white font-black">{dadosMatricula.cartorio}</span>
                      </p>
                    )}
                    {dadosMatricula.comarca && (
                      <p className="text-slate-400">
                        Comarca <span className="text-white font-black">{dadosMatricula.comarca}</span>
                      </p>
                    )}
                    {dadosMatricula.data_registro && (
                      <p className="text-slate-400">
                        Registro <span className="text-white font-black">{dadosMatricula.data_registro}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {proprietarios.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Users size={12} className="text-amber-400" />
                    <span className="text-[8px] font-black text-amber-400 uppercase tracking-widest">
                      Proprietários Identificados ({proprietarios.length})
                    </span>
                  </div>
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                    {proprietarios.map((p, idx) => (
                      <div
                        key={p.id || idx}
                        className="p-3 bg-slate-950 rounded-xl border border-white/5 space-y-1.5"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[10px] font-black text-white uppercase leading-tight">
                            {p.nome || `Proprietário ${idx + 1}`}
                          </p>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {p.averbacao_origem && (
                              <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest bg-white/5 px-2 py-0.5 rounded">
                                {p.averbacao_origem}
                              </span>
                            )}
                            {p.percentual != null && (
                              <span className="flex items-center gap-1 text-[8px] font-black text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">
                                <Percent size={9} /> {p.percentual}%
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[8px]">
                          {p.cpf && (
                            <p className="text-slate-500">
                              CPF <span className="text-slate-300 font-mono font-bold">{p.cpf}</span>
                            </p>
                          )}
                          {p.rg && (
                            <p className="text-slate-500">
                              RG <span className="text-slate-300 font-mono font-bold">{p.rg}</span>
                            </p>
                          )}
                          {p.profissao && (
                            <p className="text-slate-500 col-span-2">
                              <Briefcase size={8} className="inline mr-1" />
                              <span className="text-slate-300 font-bold">{p.profissao}</span>
                            </p>
                          )}
                          {p.estado_civil && (
                            <p className="text-slate-500 col-span-2">
                              <Heart size={8} className="inline mr-1" />
                              <span className="text-slate-300 font-bold">{p.estado_civil}</span>
                              {p.regime_bens && (
                                <span className="text-slate-500"> · {p.regime_bens}</span>
                              )}
                            </p>
                          )}
                          {p.conjuge && (
                            <p className="text-slate-500 col-span-2 truncate">
                              Cônjuge: <span className="text-slate-300 font-bold">{p.conjuge}</span>
                              {p.conjuge_cpf && (
                                <span className="text-slate-500 font-mono"> ({p.conjuge_cpf})</span>
                              )}
                            </p>
                          )}
                          {p.endereco_completo && (
                            <p className="text-slate-500 col-span-2 leading-relaxed">
                              <Home size={8} className="inline mr-1" />
                              <span className="text-slate-400">{p.endereco_completo}</span>
                            </p>
                          )}
                          {p.forma_aquisicao && (
                            <p className="text-slate-500 col-span-2">
                              Aquisição: <span className="text-slate-300 font-bold">{p.forma_aquisicao}</span>
                              {p.data_aquisicao && (
                                <span className="text-slate-500"> em {p.data_aquisicao}</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* HEADER DO FORM */}
            <div className="flex items-center gap-2 pb-2 border-b border-white/5">
              <FileText className="text-amber-400" size={14} />
              <h3 className="text-[11px] font-black text-white uppercase italic tracking-tight">
                Ou Digite Manualmente
              </h3>
            </div>

            {/* CEP */}
            <label className="block space-y-1">
              <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest">
                CEP (preenche o resto)
              </span>
              <div className="relative">
                <input
                  type="text"
                  value={dadosImovel.cep || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setDadosImovel(prev => ({ ...prev, cep: v }));
                  }}
                  onBlur={(e) => consultarCep(e.target.value)}
                  placeholder="Digite o CEP"
                  className={inputBase + ' text-lg tracking-widest font-mono'}
                />
                {buscandoCep && (
                  <RefreshCw size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 animate-spin" />
                )}
              </div>
            </label>

            <Campo
              label="Logradouro"
              value={dadosImovel.logradouro}
              onChange={v => atualizarCampo('logradouro', v)}
              onBlur={() => normalizarNoBlur('logradouro')}
              placeholder="Digite o logradouro"
              inputBase={inputBase}
            />

            <div className="flex items-center justify-between p-3 bg-slate-950 rounded-xl border border-white/5">
              <div className="flex items-center gap-2">
                <MapIcon size={14} className={dadosImovel.semNumero ? 'text-amber-400' : 'text-slate-500'} />
                <div>
                  <p className="text-[10px] font-black text-white uppercase tracking-widest leading-none">
                    Imóvel sem número
                  </p>
                  <p className="text-[7px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                    Terreno, lote vago, gleba
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={alternarSemNumero}
                className={`relative w-11 h-6 rounded-full transition-all shrink-0 ${
                  dadosImovel.semNumero ? 'bg-amber-400' : 'bg-slate-800'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                  dadosImovel.semNumero ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-[8px] font-black uppercase text-amber-400 ml-2 tracking-widest">
                  Número *
                </span>
                <input
                  type="text"
                  value={dadosImovel.numero || ''}
                  onChange={e => atualizarCampo('numero', e.target.value)}
                  onBlur={() => normalizarNoBlur('numero')}
                  placeholder="Digite o número"
                  readOnly={dadosImovel.semNumero}
                  className={inputBase + (dadosImovel.semNumero ? ' opacity-50 cursor-not-allowed' : '')}
                />
              </label>
              <Campo
                label={dadosImovel.semNumero ? 'Complemento (auto)' : 'Complemento'}
                value={dadosImovel.complemento}
                onChange={v => atualizarCampo('complemento', v)}
                placeholder={dadosImovel.semNumero ? 'Preenchido pelo Lote/Quadra/Setor' : 'Apto, bloco, sala'}
                inputBase={inputBase}
              />
            </div>

            {dadosImovel.semNumero && (
              <div className="p-3 bg-amber-400/5 border border-amber-400/20 rounded-xl space-y-3 animate-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-2">
                  <MapIcon size={12} className="text-amber-400" />
                  <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest">
                    Identificação Registral
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block space-y-1">
                    <span className="text-[7px] font-black uppercase text-amber-400 ml-2 tracking-widest">
                      Lote *
                    </span>
                    <input
                      type="text"
                      value={dadosImovel.lote || ''}
                      onChange={e => atualizarCampo('lote', e.target.value)}
                      onBlur={() => normalizarNoBlur('lote')}
                      placeholder="Lote"
                      className={inputBase}
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-[7px] font-black uppercase text-amber-400 ml-2 tracking-widest">
                      Quadra *
                    </span>
                    <input
                      type="text"
                      value={dadosImovel.quadra || ''}
                      onChange={e => atualizarCampo('quadra', e.target.value)}
                      onBlur={() => normalizarNoBlur('quadra')}
                      placeholder="Quadra"
                      className={inputBase}
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-[7px] font-black uppercase text-amber-400 ml-2 tracking-widest">
                      Setor *
                    </span>
                    <input
                      type="text"
                      value={dadosImovel.setor || ''}
                      onChange={e => atualizarCampo('setor', e.target.value)}
                      onBlur={() => normalizarNoBlur('setor')}
                      placeholder="Setor"
                      className={inputBase}
                    />
                  </label>
                </div>
              </div>
            )}

            <Campo
              label="Bairro"
              value={dadosImovel.bairro}
              onChange={v => atualizarCampo('bairro', v)}
              placeholder="Digite o bairro"
              inputBase={inputBase}
            />

            <div className="grid grid-cols-[1fr_80px] gap-3">
              <Campo
                label="Cidade"
                value={dadosImovel.cidade}
                onChange={v => atualizarCampo('cidade', v)}
                placeholder="Digite a cidade"
                inputBase={inputBase}
              />
              <Campo
                label="UF"
                value={dadosImovel.uf}
                onChange={v => atualizarCampo('uf', v)}
                onBlur={() => normalizarNoBlur('uf')}
                placeholder="UF"
                inputBase={inputBase}
              />
            </div>

            <button
              type="button"
              onClick={geocodificar}
              disabled={!enderecoMinimoOk || !numeroOk || carregando}
              className="w-full py-3 rounded-xl bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
            >
              {carregando ? <RefreshCw className="animate-spin" size={13} /> : <Search size={13} />}
              {carregando ? 'Consultando Google…' : 'Buscar localização no Google'}
            </button>

            {!numeroOk && enderecoMinimoOk && (
              <div className="p-2.5 bg-amber-400/5 border border-amber-400/20 rounded-xl flex items-center gap-2">
                <AlertTriangle size={12} className="text-amber-400 shrink-0" />
                <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest leading-tight">
                  {dadosImovel.semNumero
                    ? 'Preencha Lote, Quadra e Setor para liberar a geocodificação'
                    : 'Preencha o número para liberar a geocodificação'}
                </p>
              </div>
            )}

            <div className="p-3 bg-slate-950 rounded-xl border border-white/5 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Latitude</span>
                <span className="text-[11px] font-mono font-black text-amber-400">
                  {dadosImovel.lat || '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Longitude</span>
                <span className="text-[11px] font-mono font-black text-amber-400">
                  {dadosImovel.lng || '—'}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setEnderecoConfirmado(v => !v)}
              disabled={!geocodificacaoOk}
              className={`w-full py-3 rounded-xl font-black text-[9px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all ${
                enderecoConfirmado
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {enderecoConfirmado ? <CheckCircle2 size={13} /> : <ShieldAlert size={13} />}
              {enderecoConfirmado ? 'Endereço Confirmado' : 'Confirmar Endereço'}
            </button>

            {erro && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex gap-2">
                <AlertTriangle size={14} className="shrink-0" />
                <p className="text-[9px] font-black uppercase tracking-wide leading-relaxed">{erro}</p>
              </div>
            )}

            <button
              type="button"
              onClick={finalizar}
              disabled={!geocodificacaoOk || !enderecoConfirmado || !mapaValidado || carregando}
              className="w-full py-3.5 rounded-xl bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed transition-all"
            >
              Confirmar e Concluir Ingestão
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, value, onChange, onBlur, placeholder, inputBase }) {
  return (
    <label className="block space-y-1">
      <span className="text-[8px] font-black uppercase text-slate-500 ml-2 tracking-widest">{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className={inputBase}
      />
    </label>
  );
}